/**
 * LogsTab Component
 * Displays audit log / change history for an organization
 */
import React, { memo } from 'react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { History, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';

export const LogsTab = memo(({
  orgLogs,
  logsLoading,
  logsPage,
  logsTotalPages,
  fetchOrgLogs,
  viewingOrgId,
  formatDate
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium flex items-center gap-2">
          <History className="w-4 h-4" />
          Change History
        </h3>
        <Button variant="outline" size="sm" onClick={() => fetchOrgLogs(viewingOrgId, 1)} disabled={logsLoading}>
          {logsLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
        </Button>
      </div>
      
      {logsLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      ) : orgLogs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-lg">
          <History className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">No changes recorded</p>
          <p className="text-sm">Changes to this organization will appear here</p>
        </div>
      ) : (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {orgLogs.map((log) => (
            <div key={log.log_id} className="p-4 border rounded-lg hover:bg-muted/30 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <Badge variant={log.action === 'inserted' ? 'default' : log.action === 'removed' ? 'destructive' : 'secondary'} className="capitalize">
                      {log.action}
                    </Badge>
                    <span className="font-medium text-sm capitalize">{log.field.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="text-sm text-muted-foreground space-y-1">
                    {log.action !== 'inserted' && log.old_value && (
                      <p><span className="text-red-600">-</span> {log.old_value}</p>
                    )}
                    {log.action !== 'removed' && log.new_value && (
                      <p><span className="text-green-600">+</span> {log.new_value}</p>
                    )}
                  </div>
                </div>
                <div className="text-right text-sm text-muted-foreground shrink-0">
                  <p className="font-medium">{log.user_name}</p>
                  <p>{formatDate(log.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      
      {logsTotalPages > 1 && (
        <div className="flex items-center justify-center gap-2 pt-4 border-t">
          <Button variant="outline" size="sm" onClick={() => fetchOrgLogs(viewingOrgId, logsPage - 1)} disabled={logsPage <= 1 || logsLoading}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-sm text-muted-foreground">Page {logsPage} of {logsTotalPages}</span>
          <Button variant="outline" size="sm" onClick={() => fetchOrgLogs(viewingOrgId, logsPage + 1)} disabled={logsPage >= logsTotalPages || logsLoading}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
    </div>
  );
});

LogsTab.displayName = 'LogsTab';

export default LogsTab;
